import { Bar, BarChart, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { inr, signed, tone, fmt, fmtInt } from "../../lib/format";
import { Panel, Metric, th, td, Empty } from "../Panel";
import { EquityCurve, FlipStats } from "../motion";

function DailyChart({ daily }) {
  if (!daily.length) return <Empty>No closed trades in this range.</Empty>;
  return (
    <div className="h-56 px-2 py-3" data-testid="pnl-daily-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={daily} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
          <YAxis domain={[(m) => Math.min(0, m), (m) => Math.max(0, m)]} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={64} axisLine={false} tickLine={false} tickFormatter={(v) => fmtInt(v)} />
          <ReferenceLine y={0} stroke="hsl(var(--border))" />
          <Tooltip cursor={{ fill: "hsl(var(--accent))", opacity: 0.4 }} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 11 }}
            formatter={(v) => [inr(v), "Net P&L"]} />
          <Bar dataKey="net" radius={[2, 2, 0, 0]}>{daily.map((d) => <Cell key={d.date} fill={d.net >= 0 ? "hsl(var(--up))" : "hsl(var(--down))"} />)}</Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Stats({ s }) {
  const items = [["Trading days", s.trading_days], ["Win rate (days)", `${s.win_rate}%`], ["Avg winning day", inr(s.avg_win)], ["Avg losing day", inr(s.avg_loss)],
    ["Best day", s.best_day ? `${s.best_day.date} · ${signed(s.best_day.net)}` : "—"], ["Worst day", s.worst_day ? `${s.worst_day.date} · ${signed(s.worst_day.net)}` : "—"],
    ["Winning / losing symbols", `${s.winners} / ${s.losers}`], ["Turnover", inr(s.turnover)]];
  return (
    <div className="px-4 py-2 text-[12.5px] divide-y divide-border/60" data-testid="pnl-stats">
      {items.map(([k, v]) => <div key={k} className="flex justify-between py-2"><span className="text-muted-foreground">{k}</span><span className="num">{v}</span></div>)}
      {Object.entries(s.by_segment || {}).map(([k, v]) => (
        <div key={k} className="flex justify-between py-2"><span className="text-muted-foreground">{k} net</span><span className={`num ${tone(v.net)}`}>{signed(v.net)}</span></div>))}
    </div>
  );
}

function PnlTable({ rows }) {
  if (!rows.length) return <Empty testid="pnl-empty">No trades in this range.</Empty>;
  const H = ["Symbol", "Segment", "Buy qty", "Avg buy", "Sell qty", "Avg sell", "Realised", "Charges", "Net", "Open", "Unrealised"];
  return (
    <div className="overflow-auto thin-scroll max-h-[480px]">
      <table className="w-full" data-testid="pnl-table">
        <thead className="border-b border-border sticky top-0 bg-card"><tr>{H.map((h, i) => <th key={h} className={`${th} ${i > 1 ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={`${r.instrument_key}-${r.product}`} className="border-b border-border/60 hover:bg-accent/40" data-testid={`pnl-row-${r.symbol}-${r.product}`}>
            <td className={`${td} font-medium`}>{r.symbol}<span className="ml-1 text-[10px] text-muted-foreground">{r.product}</span></td>
            <td className={`${td} text-muted-foreground`}>{r.segment}</td>
            <td className={`${td} num text-right`}>{r.buy_qty}</td><td className={`${td} num text-right`}>{fmt(r.avg_buy)}</td>
            <td className={`${td} num text-right`}>{r.sell_qty}</td><td className={`${td} num text-right`}>{fmt(r.avg_sell)}</td>
            <td className={`${td} num text-right ${tone(r.realised)}`}>{signed(r.realised)}</td>
            <td className={`${td} num text-right text-muted-foreground`}>{fmt(r.charges)}</td>
            <td className={`${td} num text-right font-semibold ${tone(r.net)}`}>{signed(r.net)}</td>
            <td className={`${td} num text-right`}>{r.open_qty || "—"}</td>
            <td className={`${td} num text-right ${tone(r.unrealised)}`}>{r.open_qty ? signed(r.unrealised) : "—"}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

export function PnlReport({ data }) {
  const s = data.summary;
  return (
    <div className="space-y-4" data-testid="pnl-report">
      <Panel>
        <div className="grid grid-cols-2 lg:grid-cols-5 [&>*]:border-border [&>*]:border-b lg:[&>*]:border-b-0 lg:divide-x divide-border">
          <Metric label="Net realised P&L" value={<span className={tone(s.net)}>{signed(s.net)}</span>} testid="pnl-net" />
          <Metric label="Gross realised" value={<span className={tone(s.realised)}>{signed(s.realised)}</span>} testid="pnl-gross" />
          <Metric label="Charges" value={inr(s.charges)} testid="pnl-charges" />
          <Metric label="Unrealised (open)" value={<span className={tone(s.unrealised)}>{signed(s.unrealised)}</span>} testid="pnl-unrealised" />
          <Metric label="Trades" value={s.trades} testid="pnl-trades" />
        </div>
      </Panel>
      <Panel title="Analytics summary"><FlipStats s={s} daily={data.daily} /></Panel>
      <Panel title="Equity curve · from ledger"><EquityCurve days={365} height={200} /></Panel>
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4 items-start">
        <Panel title="Daily net P&L"><DailyChart daily={data.daily} /></Panel>
        <Panel title="Analytics"><Stats s={s} /></Panel>
      </div>
      <Panel title="Symbol-wise P&L"><PnlTable rows={data.rows} /></Panel>
    </div>
  );
}
